import axios from 'axios'
import type {
  GlobalStats,
  Kill,
  MapStat,
  Mission,
  MissionPlayer,
  Paginated,
  PlayerCompare,
  PlayerDetail,
  PlayerLeaderboard,
  Timeseries,
  TopPlayer,
  UploadResult,
  WeaponStat,
} from '@/types'

const api = axios.create({
  baseURL: '/api',
  timeout: 60000,
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('slv_admin_token')
  if (token && config.url?.startsWith('/admin')) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

export async function getHealth() {
  const { data } = await api.get('/health')
  return data
}

export async function getGlobalStats(): Promise<GlobalStats> {
  const { data } = await api.get<{ data: GlobalStats }>('/stats')
  return data.data
}

export async function getMissions(page = 1, limit = 20, search = '') {
  const { data } = await api.get<Paginated<Mission[]>>('/missions', {
    params: { page, limit, search: search || undefined },
  })
  return data
}

export async function getMission(id: string | number) {
  const { data } = await api.get<{ data: Mission }>(`/missions/${id}`)
  return data.data
}

export async function getMissionStats(id: string | number) {
  const { data } = await api.get<{
    data: {
      mission: Mission
      topPlayers: TopPlayer[]
      avgFPS: number
      totalKills: number
      playerCount: number
    }
  }>(`/missions/${id}/stats`)
  return data.data
}

export async function getMissionPlayers(id: string | number, side?: string) {
  const { data } = await api.get<{ data: MissionPlayer[] }>(`/missions/${id}/players`, {
    params: side ? { side } : undefined,
  })
  return data.data
}

export async function getMissionTimeseries(id: string | number) {
  const { data } = await api.get<{ data: Timeseries }>(`/missions/${id}/timeseries`)
  return data.data
}

export async function getMissionKillfeed(id: string | number, page = 1, limit = 50) {
  const { data } = await api.get<Paginated<Kill[]>>(`/missions/${id}/killfeed`, {
    params: { page, limit },
  })
  return data
}

export async function getMissionWeapons(id: string | number) {
  const { data } = await api.get<{ data: WeaponStat[] }>(`/missions/${id}/weapons`)
  return data.data
}

export async function getPlayers(page = 1, limit = 25, search = '', sort = 'kills') {
  const { data } = await api.get<Paginated<PlayerLeaderboard[]>>('/players', {
    params: { page, limit, search: search || undefined, sort },
  })
  return data
}

export async function getPlayer(id: string | number) {
  const { data } = await api.get<{ data: PlayerDetail }>(`/players/${id}`)
  return data.data
}

export async function comparePlayers(id1: string | number, id2: string | number) {
  const { data } = await api.get<{ data: { player1: PlayerCompare; player2: PlayerCompare } }>(
    `/players/compare/${id1}/${id2}`,
  )
  return data.data
}

export async function getWeapons(limit = 50) {
  const { data } = await api.get<{ data: WeaponStat[] }>('/weapons', { params: { limit } })
  return data.data
}

export async function getMaps() {
  const { data } = await api.get<{ data: MapStat[] }>('/maps')
  return data.data
}

export async function adminLogin(password: string) {
  const { data } = await api.post<{ token: string }>('/admin/login', { password })
  return data
}

export async function adminDashboard() {
  const { data } = await api.get('/admin/dashboard')
  return data.data
}

export async function adminGetMissions(page = 1, limit = 20, search = '', hidden = '') {
  const { data } = await api.get('/admin/missions', {
    params: { page, limit, search: search || undefined, hidden: hidden || undefined },
  })
  return data
}

export async function adminGetMission(id: string | number) {
  const { data } = await api.get(`/admin/missions/${id}`)
  return data.data
}

export async function adminUpdateMission(id: string | number, body: Record<string, unknown>) {
  const { data } = await api.put(`/admin/missions/${id}`, body)
  return data
}

export async function adminToggleHidden(id: string | number) {
  const { data } = await api.patch(`/admin/missions/${id}/toggle-hidden`)
  return data
}

export async function adminDeleteMission(id: string | number) {
  const { data } = await api.delete(`/admin/missions/${id}`)
  return data
}

export async function adminGetKills(id: string | number, page = 1, limit = 50) {
  const { data } = await api.get(`/admin/missions/${id}/kills`, { params: { page, limit } })
  return data
}

export async function adminUpdateKill(id: string | number, body: Record<string, unknown>) {
  const { data } = await api.put(`/admin/kills/${id}`, body)
  return data
}

export async function adminDeleteKill(id: string | number) {
  const { data } = await api.delete(`/admin/kills/${id}`)
  return data
}

export async function adminUpload(files: File[]): Promise<UploadResult[]> {
  const form = new FormData()
  for (const f of files) {
    form.append('logfile', f)
  }
  const { data } = await api.post<{ results: UploadResult[] }>('/admin/upload', form)
  return data.results
}

export default api
